import { ConnectorPlatform, Theme } from '@logto/schemas';
import { useContext, useEffect, useRef, useState } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import { getOverseasBrand } from '@/hooks/use-native-sign-in';
import initI18n from '@/i18n/init';
import type { PreviewConfig, SignInExperienceResponse } from '@/types';
import { filterPreviewSocialConnectors } from '@/utils/social-connectors';

import { shouldForceLightTheme } from './use-theme';

const usePreview = () => {
  const latestPreview = useRef<PreviewConfig>();
  const [previewConfig, setPreviewConfig] = useState<PreviewConfig>();
  const { setTheme, setPlatform, setExperienceSettings } = useContext(PageContext);

  // Fetch the preview config
  useEffect(() => {
    // Listen to the message from the ancestor window
    const previewMessageHandler = async (event: MessageEvent) => {
      // #event.data should be guarded at the provider's side
      if (event.data.sender === 'ac_preview') {
        // eslint-disable-next-line no-restricted-syntax
        const previewConfig = event.data.config as PreviewConfig;

        // eslint-disable-next-line @silverhand/fp/no-mutation -- Track the newest asynchronous preview request.
        latestPreview.current = previewConfig;

        // Wait for i18n to be initialized
        await initI18n(
          previewConfig.language,
          getOverseasBrand(previewConfig.signInExperience.customContent.evaEdition)
            ? 'overseas'
            : 'domestic',
          () => latestPreview.current === previewConfig
        );

        if (latestPreview.current === previewConfig) {
          setPreviewConfig(previewConfig);
        }
      }
    };

    window.addEventListener('message', previewMessageHandler);

    return () => {
      // eslint-disable-next-line @silverhand/fp/no-mutation -- Invalidate pending preview work on unmount.
      latestPreview.current = undefined;
      window.removeEventListener('message', previewMessageHandler);
    };
  }, []);

  // Set Experience settings
  useEffect(() => {
    if (!previewConfig) {
      return;
    }

    const {
      signInExperience: { socialConnectors, ...rest },
      isNative,
    } = previewConfig;

    const experienceSettings: SignInExperienceResponse = {
      ...rest,
      socialConnectors: filterPreviewSocialConnectors(
        isNative ? ConnectorPlatform.Native : ConnectorPlatform.Web,
        socialConnectors
      ),
    };

    setExperienceSettings(experienceSettings);
  }, [previewConfig, setExperienceSettings]);

  // Set Theme
  useEffect(() => {
    if (previewConfig?.mode) {
      setTheme(
        shouldForceLightTheme(previewConfig.signInExperience) ? Theme.Light : previewConfig.mode
      );
    }
  }, [previewConfig, setTheme]);

  // Set Platform
  useEffect(() => {
    if (previewConfig?.platform) {
      setPlatform(previewConfig.platform);
    }
  }, [previewConfig?.platform, setPlatform]);
};

export default usePreview;
